import React, { useLayoutEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import ScrollIntoView from "react-scroll-into-view"

import {
  Backdrop,
  Container,
  Content,
  HeadlineRow,
  TitleCol,
  Eyebrow,
  Title,
  LedeCol,
  Lede,
  CornerField,
  MoleculeGrid,
  MoleculeOne,
  MoleculeTwo,
  MoleculeThree,
  MoleculeFour,
  MoleculeFive,
  FallOne,
  FallTwo,
  FallThree,
  FallFour,
  FallFive,
  FlowBox,
  FlowRow,
  Stage,
  StageArrow,
  StageTag,
  ResultCardsRow,
  ResultCard,
  ResultTop,
  ResultNumber,
  ResultHeading,
  ResultBody,
  NextButtonRow,
  NextButton,
} from "./styles"

import Molecules from "../../../assets/molecules.svg"
import Molecule1 from "../../../assets/molecule1.svg"
import Molecule2 from "../../../assets/molecule2.svg"
import Molecule3 from "../../../assets/molecule3.svg"
import Molecule4 from "../../../assets/molecule4.svg"

import DataTerminal from "./DataTerminal"
import NeuronBrain from "./NeuronBrain"
import MoleculeGrowth from "./MoleculeGrowth"

const iconProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
}

const NovelChemistryIcon = () => (
  <svg {...iconProps}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
  </svg>
)

const SustainableIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3c4 3 6 6.2 6 9.5A6 6 0 0 1 6 12.5C6 9.2 8 6 12 3Z" />
    <path d="M12 12.5v7" />
  </svg>
)

const EverydayIcon = () => (
  <svg {...iconProps}>
    <path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3Z" />
    <path d="M4.5 7.5 12 12l7.5-4.5M12 12v9" />
  </svg>
)

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

const DEFAULT_RESULTS = [
  {
    label: "Novel chemistry",
    text: "Precise control over selectivity unlocks synthesis routes conventional chemistry can't reach.",
    Icon: NovelChemistryIcon,
    number: "01",
  },
  {
    label: "Sustainable by design",
    text: "Less energy, less waste, less reliance on fossil feedstocks.",
    Icon: SustainableIcon,
    number: "02",
  },
  {
    label: "Natural everyday ingredients",
    text: "Direct pathways from renewable and upcycled feedstocks to the ingredients in everyday products — cosmetics, personal care, food and nutrition.",
    Icon: EverydayIcon,
    number: "03",
  },
]

const TwoColGrid = ({ eyebrow, title, lede, points }) => {
  const results =
    points && points.length === DEFAULT_RESULTS.length
      ? points.map((text, i) => ({ ...DEFAULT_RESULTS[i], text }))
      : DEFAULT_RESULTS

  const [titleLine1, ...rest] = (title || "").split(",")
  const titleLine2 = rest.join(",").trim()

  // State — exact original scroll-trigger mechanism from main
  const [show, doShow] = useState({
    itemOne: false,
    itemTwo: false,
    itemThree: false,
    itemFour: false,
    itemFive: false,
  })

  const refOne = useRef(null),
    refTwo = useRef(null),
    refThree = useRef(null),
    refFour = useRef(null),
    refFive = useRef(null)

  useLayoutEffect(() => {
    const topPos = element => element.getBoundingClientRect().top

    const div1Pos = topPos(refOne.current),
      div2Pos = topPos(refTwo.current),
      div3Pos = topPos(refThree.current),
      div4Pos = topPos(refFour.current),
      div5Pos = topPos(refFive.current)

    const onScroll = () => {
      const scrollPos = window.scrollY + window.innerHeight
      div1Pos < scrollPos && doShow(state => ({ ...state, itemOne: true }))
      div2Pos < scrollPos && doShow(state => ({ ...state, itemTwo: true }))
      div3Pos < scrollPos && doShow(state => ({ ...state, itemThree: true }))
      div4Pos < scrollPos && doShow(state => ({ ...state, itemFour: true }))
      div5Pos < scrollPos && doShow(state => ({ ...state, itemFive: true }))
    }

    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  return (
    <Backdrop id="technology">
      <Container>
        <CornerField>
          <FallOne animate={show.itemOne}>
            <MoleculeOne src={Molecule1} alt="" animate={show.itemOne} ref={refOne} />
          </FallOne>
          <FallTwo animate={show.itemTwo}>
            <MoleculeTwo src={Molecule2} alt="" animate={show.itemTwo} ref={refTwo} />
          </FallTwo>
          <FallThree animate={show.itemThree}>
            <MoleculeThree src={Molecule3} alt="" animate={show.itemThree} ref={refThree} />
          </FallThree>
          <FallFour animate={show.itemFour}>
            <MoleculeFour src={Molecule4} alt="" animate={show.itemFour} ref={refFour} />
          </FallFour>
          <FallFive animate={show.itemFive}>
            <MoleculeFive src={Molecule1} alt="" animate={show.itemFive} ref={refFive} />
          </FallFive>
          <MoleculeGrid src={Molecules} alt="" />
        </CornerField>

        <Content>
          <HeadlineRow>
            <TitleCol>
              {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
              <Title>
                <span>
                  {titleLine1}
                  {titleLine2 ? "," : ""}
                </span>
                {titleLine2 ? <span>{titleLine2}</span> : null}
              </Title>
            </TitleCol>

            <LedeCol>{lede ? <Lede>{lede}</Lede> : null}</LedeCol>
          </HeadlineRow>

          <FlowBox>
            <FlowRow>
              <Stage>
                <DataTerminal />
                <StageTag>
                  <span>[</span> Novel enzyme data <span>]</span>
                </StageTag>
              </Stage>

              <StageArrow>
                <ArrowIcon />
              </StageArrow>

              <Stage>
                <NeuronBrain />
                <StageTag>
                  <span>[</span> Predictive &amp; generative ML <span>]</span>
                </StageTag>
              </Stage>

              <StageArrow>
                <ArrowIcon />
              </StageArrow>

              <Stage>
                <MoleculeGrowth />
                <StageTag>
                  <span>[</span> Cell-free biocatalysis <span>]</span>
                </StageTag>
              </Stage>
            </FlowRow>
          </FlowBox>

          <ResultCardsRow>
            {results.map(({ label, text, Icon, number }) => (
              <ResultCard key={label}>
                <ResultTop>
                  <Icon />
                  {number ? <ResultNumber>{number}</ResultNumber> : null}
                </ResultTop>
                <ResultHeading>{label}</ResultHeading>
                <ResultBody>{text}</ResultBody>
              </ResultCard>
            ))}
          </ResultCardsRow>

          <NextButtonRow>
            <ScrollIntoView selector="#proprietary-data">
              <NextButton>
                Proprietary data
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14M5 12l7 7 7-7" />
                </svg>
              </NextButton>
            </ScrollIntoView>
          </NextButtonRow>
        </Content>
      </Container>
    </Backdrop>
  )
}

TwoColGrid.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string,
  lede: PropTypes.string,
  points: PropTypes.arrayOf(PropTypes.string),
}

export default TwoColGrid
